import { createFileRoute } from '@tanstack/react-router'
import { CURRENT_SCHEMA_VERSION } from '@/core/projects/migrations'
import { requireAuthBeforeLoad } from '@/features/auth/guards'
import {
  getWorkspaceHandleRecord,
  queryHandlePermission,
} from '@/infrastructure/storage/handles-db'
import { getProject } from '@/infrastructure/storage'
import { getWorkspaceRoot, setWorkspaceRoot } from '@/infrastructure/storage/workspace-fs/root'

type EditorProjectSearch = {
  assistant?: boolean
}

async function ensureWorkspaceRootForEditorLoader(): Promise<void> {
  if (getWorkspaceRoot()) {
    return
  }

  const record = await getWorkspaceHandleRecord()
  if (!record) {
    return
  }

  const handle = record.handle as FileSystemDirectoryHandle
  const permission = await queryHandlePermission(handle)
  if (permission === 'granted') {
    setWorkspaceRoot(handle)
  }
}

export const Route = createFileRoute('/editor/$projectId')({
  // Editor loader data is tiny and migration state must be fresh on reopen.
  // Avoid keeping inactive editor matches around with stale "requires upgrade" flags.
  gcTime: 0,
  preloadGcTime: 0,
  validateSearch: (search: Record<string, unknown>): EditorProjectSearch => ({
    assistant: search.assistant === true || search.assistant === 'true',
  }),
  beforeLoad: ({ location }) => requireAuthBeforeLoad({ location }),
  loader: async ({ params }) => {
    await ensureWorkspaceRootForEditorLoader()

    // Validate project exists - actual loading happens in Editor via loadTimeline
    const project = await getProject(params.projectId)

    if (!project) {
      throw new Error(`Project not found: ${params.projectId}`)
    }

    const storedSchemaVersion = project.schemaVersion ?? 1

    // Only pass metadata needed for Editor initialization (not timeline data)
    return {
      project: {
        id: project.id,
        name: project.name,
        width: project.metadata.width,
        height: project.metadata.height,
        fps: project.metadata.fps,
        backgroundColor: project.metadata.backgroundColor,
      },
      migration: {
        storedSchemaVersion,
        currentSchemaVersion: CURRENT_SCHEMA_VERSION,
        requiresUpgrade: storedSchemaVersion < CURRENT_SCHEMA_VERSION,
      },
    }
  },
})
